/**
 * Locale-aware presentation helpers.
 *
 * Every server timestamp on the wire is an ISO-8601 UTC string
 * (`2025-01-15T14:30:00.000Z`). The browser is the only place that knows the
 * reader's locale and time zone, so formatting happens here — never on the
 * server, never with a hardcoded locale string, and never with a date library.
 * `Intl.DateTimeFormat` ships with the runtime and is the only formatter used.
 *
 * Resolution order for the locale:
 *   1. explicit `locale` argument (user preference override),
 *   2. `navigator.language` (browser preference),
 *   3. `DEFAULT_LOCALE` (`en-US`) when neither is available (SSR / node tests).
 */

/** Fallback when neither the caller nor the browser expresses a preference. */
export const DEFAULT_LOCALE = 'en-US';

/** Rendered in place of a missing / unparseable timestamp. */
export const EMPTY_VALUE_PLACEHOLDER = '—';

type Nullable = string | null | undefined;

/**
 * Resolve the locale to format with. Exported so callers that need a formatter
 * of their own (charts, CSV export) stay consistent with the rest of the UI.
 */
export function resolveLocale(locale?: Nullable): string {
  if (typeof locale === 'string' && locale.trim() !== '') {
    return locale;
  }
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    if (navigator.language.trim() !== '') {
      return navigator.language;
    }
  }
  return DEFAULT_LOCALE;
}

/** Parse an ISO-8601 string, returning `null` for missing or invalid input. */
export function parseTimestamp(iso: Nullable): Date | null {
  if (typeof iso !== 'string' || iso.trim() === '') {
    return null;
  }
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * `Intl.DateTimeFormat` construction throws a `RangeError` on a malformed
 * locale tag (a user can persist anything). Fall back rather than blow up a
 * whole screen over a preference string.
 */
function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale, options);
  } catch {
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, options);
  }
}

function apply(
  iso: Nullable,
  locale: Nullable,
  options: Intl.DateTimeFormatOptions,
  placeholder: string
): string {
  const date = parseTimestamp(iso);
  if (date === null) {
    return placeholder;
  }
  return formatter(resolveLocale(locale), options).format(date);
}

const TIMESTAMP_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const SHORT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

const FULL_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: 'full',
  timeStyle: 'long',
};

/** `Jan 15, 2025, 02:30 PM` (en-US) — the default table/detail rendering. */
export function formatTimestamp(iso: Nullable, locale?: Nullable): string {
  return apply(iso, locale, TIMESTAMP_OPTIONS, EMPTY_VALUE_PLACEHOLDER);
}

/** `1/15/25` (en-US), `15.1.25` (de-DE) — dense table cells. */
export function formatShortDate(iso: Nullable, locale?: Nullable): string {
  return apply(iso, locale, SHORT_DATE_OPTIONS, EMPTY_VALUE_PLACEHOLDER);
}

/** `Jan 15, 2025` — date without a time component. */
export function formatDate(iso: Nullable, locale?: Nullable): string {
  return apply(iso, locale, DATE_OPTIONS, EMPTY_VALUE_PLACEHOLDER);
}

/** `02:30 PM` — time without a date component. */
export function formatTime(iso: Nullable, locale?: Nullable): string {
  return apply(iso, locale, TIME_OPTIONS, EMPTY_VALUE_PLACEHOLDER);
}

/**
 * Long form including the time zone, for the `title` tooltip that accompanies
 * an abbreviated timestamp. Returns an empty string for missing input so it can
 * be spread straight onto `title={...}` without rendering a dangling dash.
 */
export function formatTimestampTitle(iso: Nullable, locale?: Nullable): string {
  return apply(iso, locale, FULL_OPTIONS, '');
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * `in 3 minutes` / `2 hours ago`. Falls back to the absolute timestamp beyond a
 * month, where relative phrasing stops being useful.
 */
export function formatRelativeTime(iso: Nullable, locale?: Nullable, nowMs?: number): string {
  const date = parseTimestamp(iso);
  if (date === null) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  const resolved = resolveLocale(locale);
  const deltaMs = date.getTime() - (nowMs ?? Date.now());
  const absMs = Math.abs(deltaMs);
  if (absMs >= 30 * DAY_MS) {
    return formatDate(iso, resolved);
  }

  let unit: Intl.RelativeTimeFormatUnit = 'second';
  let value = Math.round(deltaMs / 1000);
  if (absMs >= DAY_MS) {
    unit = 'day';
    value = Math.round(deltaMs / DAY_MS);
  } else if (absMs >= HOUR_MS) {
    unit = 'hour';
    value = Math.round(deltaMs / HOUR_MS);
  } else if (absMs >= MINUTE_MS) {
    unit = 'minute';
    value = Math.round(deltaMs / MINUTE_MS);
  }

  try {
    return new Intl.RelativeTimeFormat(resolved, { numeric: 'auto' }).format(value, unit);
  } catch {
    return new Intl.RelativeTimeFormat(DEFAULT_LOCALE, { numeric: 'auto' }).format(value, unit);
  }
}

/** Locale-aware integer/decimal rendering (thousands separators per locale). */
export function formatNumber(
  value: number | null | undefined,
  locale?: Nullable,
  options?: Intl.NumberFormatOptions
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return EMPTY_VALUE_PLACEHOLDER;
  }
  try {
    return new Intl.NumberFormat(resolveLocale(locale), options).format(value);
  } catch {
    return new Intl.NumberFormat(DEFAULT_LOCALE, options).format(value);
  }
}

/** Clamp free text for a summary cell, appending an ellipsis when cut. */
export function truncate(text: Nullable, maxLength: number): string {
  if (typeof text !== 'string' || text === '') {
    return '';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}
